import React, { useState, useEffect } from 'react';
import { 
  Drawer, Box, Button, Typography, Divider, 
  FormControl, InputLabel, Select, MenuItem,
  Stack, IconButton, TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Panel de filtros avanzados para órdenes
 * Diseño premium con mejor UX que el panel nativo de Tiendanube
 */
const FiltersPanel = ({ 
  open, 
  onClose, 
  filters, 
  setFilters, 
  resetFilters,
  onApply
}) => {
  // Estado local para no aplicar filtros hasta que el usuario confirme
  const [localFilters, setLocalFilters] = useState(filters);
  
  // Sincronizar estado local con props cuando se abre el panel
  useEffect(() => {
    if (open) {
      setLocalFilters({...filters});
    }
  }, [open, filters]);
  
  // Manejar cambios en los filtros
  const handleFilterChange = (field, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Aplicar filtros y cerrar
  const handleApply = () => {
    setFilters(localFilters);
    if (onApply) onApply();
  };
  
  // Reiniciar filtros
  const handleReset = () => {
    const emptyFilters = {
      startDate: null,
      endDate: null,
      paymentStatus: 'all',
      shippingStatus: 'all'
    };
    setLocalFilters(emptyFilters);
    resetFilters();
  };
  
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { 
          width: { xs: '100%', sm: 420 },
          p: 3
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Filtros avanzados</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      {/* Filtros por fecha */}
      <Typography variant="subtitle2" sx={{ mb: 2 }}>Rango de fechas</Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        <DatePicker
          label="Desde"
          value={localFilters.startDate}
          onChange={(date) => handleFilterChange('startDate', date)}
          renderInput={(params) => <TextField {...params} size="small" fullWidth />}
        />
        <DatePicker
          label="Hasta"
          value={localFilters.endDate}
          onChange={(date) => handleFilterChange('endDate', date)}
          renderInput={(params) => <TextField {...params} size="small" fullWidth />}
        />
      </Stack>
      
      {/* Estado de pago */}
      <FormControl fullWidth size="small" sx={{ mb: 3 }}>
        <InputLabel>Estado de pago</InputLabel>
        <Select
          value={localFilters.paymentStatus || 'all'}
          onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
          label="Estado de pago"
        >
          <MenuItem value="all">Todos</MenuItem>
          <MenuItem value="paid">Pagado</MenuItem>
          <MenuItem value="pending">Pendiente</MenuItem>
          <MenuItem value="failed">Fallido</MenuItem>
        </Select>
      </FormControl>
      
      {/* Estado de envío */}
      <FormControl fullWidth size="small" sx={{ mb: 3 }}>
        <InputLabel>Estado de envío</InputLabel>
        <Select
          value={localFilters.shippingStatus || 'all'}
          onChange={(e) => handleFilterChange('shippingStatus', e.target.value)}
          label="Estado de envío"
        >
          <MenuItem value="all">Todos</MenuItem>
          <MenuItem value="shipped">Enviado</MenuItem>
          <MenuItem value="delivered">Entregado</MenuItem>
          <MenuItem value="pending">Pendiente</MenuItem>
        </Select>
      </FormControl>
      
      <Box sx={{ mt: 'auto', pt: 3, display: 'flex', justifyContent: 'space-between' }}>
        <Button 
          variant="outlined"
          onClick={handleReset}
        >
          Reiniciar
        </Button>
        <Button 
          variant="contained"
          onClick={handleApply}
          color="primary"
        >
          Aplicar filtros
        </Button>
      </Box>
    </Drawer>
  );
};

export default FiltersPanel;
